import {
  ClientInsightStatus,
  IClientInsightModel,
  IClientInsightReason,
  IClientProfileData,
  IClientProfileResponse,
} from './interfaces';

/** Модель карточки без имени клиента — имя проставляется на стороне диалога/страницы. */
export type ClientInsightData = Omit<IClientInsightModel, 'clientName'>;

/** Человекочитаемые ярлыки технических ключей источников из status_reasoning. */
const SOURCE_LABELS: Record<string, string> = {
  source_1_last_requests: 'Заявки',
  source_2_service_calls: 'Звонки',
  source_3_domovoy_gradusnik: 'Градусник',
};

const mapSource = (source: string): string => SOURCE_LABELS[source] ?? source;

/**
 * Статус-светофор бэкенд не отдаёт — выводим эвристикой на фронте:
 * красный — есть невыполненные обещания; жёлтый — есть противоречия или повторяющиеся
 * темы; иначе зелёный. Общий для операторской и менеджерской карточек.
 */
const resolveStatus = (profile: IClientProfileData): ClientInsightStatus => {
  if ((profile.unmet_promises?.length ?? 0) > 0) return 'red';
  if ((profile.contradictions?.length ?? 0) > 0 || (profile.recurring_themes?.length ?? 0) > 0) return 'yellow';
  return 'green';
};

/** Общая аналитика (причины и «что нового») — одинакова для оператора и менеджера. */
const mapReasons = (profile: IClientProfileData): IClientInsightReason[] =>
  (profile.status_reasoning ?? [])
    .filter((reason) => Boolean(reason['вывод']))
    .map((reason) => ({
      text: reason['вывод'] as string,
      sources: (reason['источники'] ?? []).map(mapSource),
    }));

const mapWhatsNew = (profile: IClientProfileData): string | undefined => {
  const changes = typeof profile.changes_since_last === 'string' ? profile.changes_since_last : '';
  return changes || profile.trend || undefined;
};

/**
 * Операторская карточка: готовая фраза (opening_line) + рекомендуемое действие + аналитика.
 * Возвращает null, если operator_card во внешнем ответе отсутствует.
 */
export const mapClientProfileToOperatorInsight = (response: IClientProfileResponse): ClientInsightData | null => {
  const profile = response.profile ?? {};
  if (!profile.operator_card) return null;

  return {
    status: resolveStatus(profile),
    recommendedAction: profile.operator_card.next_best_action ?? '',
    openingPhrase: profile.operator_card.opening_line ?? '',
    reasons: mapReasons(profile),
    whatsNew: mapWhatsNew(profile),
  };
};

/**
 * Менеджерская карточка: саммари (manager_card.summary) + рекомендуемое действие + аналитика.
 * «Готовой фразы» у менеджера нет. Возвращает null, если manager_card отсутствует.
 */
export const mapClientProfileToManagerInsight = (response: IClientProfileResponse): ClientInsightData | null => {
  const profile = response.profile ?? {};
  if (!profile.manager_card) return null;

  return {
    status: resolveStatus(profile),
    summary: profile.manager_card.summary ?? undefined,
    recommendedAction: profile.manager_card.next_best_action ?? '',
    openingPhrase: '',
    reasons: mapReasons(profile),
    whatsNew: mapWhatsNew(profile),
  };
};
